import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type PipelineResultData,
  type UploadResponse,
  type DuplicateInfo,
  type BatchDuplicate,
  type BatchStatus,
  DuplicateError,
  uploadDocument,
  getDocument,
  getBatchStatus,
} from '../services/api';
import { useT } from '../i18n';
import { useClienteAtivo } from '../contexts/ClienteAtivoContext';
import { useFormatters } from '../i18n/format';
import { type NavView } from '../components/Layout';

interface UploadedFile {
  file: File;
  status: 'pending' | 'uploading' | 'processing' | 'done' | 'error';
  progress: number;
  result?: UploadResponse;
  resultIndex?: number;
  error?: string;
}

const ACCEPTED_EXTENSIONS = ['.pdf', '.xml', '.eml', '.msg', '.zip', '.html', '.png', '.jpg', '.jpeg'];

function isAccepted(filename: string): boolean {
  const lower = filename.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function Upload({ userRole, onNavigate }: { userRole?: string; onNavigate?: (view: NavView) => void }) {
  const { clienteAtivoId } = useClienteAtivo();
  const t = useT();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedResult, setSelectedResult] = useState<PipelineResultData | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Issue #219: Estado do modal de confirmacao de duplicata.
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateInfo | null>(null);
  const [duplicateFiles, setDuplicateFiles] = useState<File[] | null>(null);
  const [duplicatePendingIndices, setDuplicatePendingIndices] = useState<number[]>([]);
  const [resolvingDuplicate, setResolvingDuplicate] = useState(false);
  // Issue #219: Estado para batch duplicates (lista de duplicatas do lote).
  const [batchDuplicates, setBatchDuplicates] = useState<BatchDuplicate[]>([]);
  const [duplicateDecisions, setDuplicateDecisions] = useState<Record<number, 'replace' | 'archive' | 'duplicate' | 'cancel'>>({});
  // Issue #279: Estado do lote (batch) ativo. Quando 3+ arquivos sao
  // enviados, o backend registra um lote e o frontend faz polling de
  // /batch-status ate concluir. Durante o lote, novos uploads sao bloqueados.
  const [batchActive, setBatchActive] = useState(false);
  const [batchStatus, setBatchStatus] = useState<BatchStatus | null>(null);
  // Toast simples (nao usa biblioteca externa). Auto-dismiss apos 5s.
  const [toast, setToast] = useState<{ message: string; type: 'info' | 'success' | 'error' } | null>(null);
  // Issue #280: Erro de rede no envio de batch — o lote pode estar sendo
  // processado no servidor. Mantem o botao desabilitado e mostra aviso em
  // vez de re-habilitar (evita re-envio e subprocesses duplicados).
  const [batchNetworkError, setBatchNetworkError] = useState(false);

  const showToast = useCallback((message: string, type: 'info' | 'success' | 'error' = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  // Issue #279: Polling de status do lote ativo. Quando batchActive e true,
  // consulta /batch-status a cada 3s. Quando o lote conclui (active=false),
  // mostra toast de conclusao e desbloqueia o upload.
  useEffect(() => {
    if (!batchActive) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const status = await getBatchStatus();
        if (cancelled) return;
        setBatchStatus(status);
        if (!status.active) {
          // Lote concluido — mostra toast de conclusao.
          const prev = batchStatusRef.current;
          if (prev && prev.active && prev.total) {
            const success = status.concluido ?? prev.concluido ?? 0;
            const errors = status.erro ?? prev.erro ?? 0;
            if (errors > 0) {
              showToast(t('upload.batch.complete', { success, errors }), 'success');
            } else {
              showToast(t('upload.batch.complete', { success, errors: 0 }), 'success');
            }
          }
          setBatchActive(false);
          setBatchStatus(null);
          batchStatusRef.current = null;
        } else {
          batchStatusRef.current = status;
        }
      } catch {
        // Erro silencioso — continua tentando.
      }
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchActive]);

  // Ref para armazenar o ultimo status do batch (evita stale closure no polling).
  const batchStatusRef = useRef<BatchStatus | null>(null);

  const addFiles = useCallback((fileList: FileList | File[]) => {
    // Issue #279: Bloqueia adicao de arquivos durante lote ativo.
    if (batchActive) {
      showToast(t('upload.batch.blocked'), 'error');
      return;
    }
    const newFiles: UploadedFile[] = Array.from(fileList)
      .filter((f) => isAccepted(f.name))
      .map((f) => ({ file: f, status: 'pending' as const, progress: 0 }));
    if (newFiles.length > 0) {
      setFiles((prev) => [...prev, ...newFiles]);
    }
  }, [batchActive, showToast, t]);

  // Issue #276: Polling de status para processamento assincrono.
  // Quando o backend retorna 202 com status "pendente" (result sem `root`),
  // o pipeline ainda roda em background (ThreadPoolExecutor). Esta funcao
  // consulta GET /api/documents/{id} a cada 3s ate o status mudar para
  // concluido/revisao/processado (sucesso) ou erro. A atualizacao do estado
  // casa pela referencia do objeto `baseResult` + resultIndex (robusto contra
  // remocao de arquivos da lista e contra multiplos arquivos do mesmo lote).
  const pollAsyncDocument = useCallback(
    async (docId: string, batchIdx: number, baseResult: UploadResponse) => {
      // Issue #267: pipeline pode levar 4-10 min (timeout global backend =
      // 900s, issue #334). O polling precisa cobrir essa janela — 300*3s =
      // 15 min. Antes (80 tentativas = 4 min) o frontend expirava e mostrava
      // "timeout" enquanto o backend ainda processava.
      const maxAttempts = 300; // 300 * 3s = 15 min
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        try {
          const detail = await getDocument(docId);
          const st = detail.document.status;
          if (st === 'concluido' || st === 'revisao' || st === 'processado') {
            const newResult: UploadResponse = {
              ...baseResult,
              documents: baseResult.documents.map((d, i) =>
                i === batchIdx ? detail.document : d,
              ),
              results: baseResult.results.map((r, i) =>
                i === batchIdx ? detail.pipeline_result : r,
              ) as UploadResponse['results'],
            };
            setFiles((prev) =>
              prev.map((f) =>
                f.result === baseResult && f.resultIndex === batchIdx
                  ? {
                      ...f,
                      status: 'done' as const,
                      progress: 100,
                      result: newResult,
                    }
                  : f,
              ),
            );
            return;
          }
          if (st === 'erro') {
            setFiles((prev) =>
              prev.map((f) =>
                f.result === baseResult && f.resultIndex === batchIdx
                  ? {
                      ...f,
                      status: 'error' as const,
                      error: detail.document.review_reason || t('common.error'),
                    }
                  : f,
              ),
            );
            return;
          }
          // Ainda pendente/processando — continua polling.
        } catch (err) {
          setFiles((prev) =>
            prev.map((f) =>
              f.result === baseResult && f.resultIndex === batchIdx
                ? {
                    ...f,
                    status: 'error' as const,
                    error: err instanceof Error ? err.message : t('common.error'),
                  }
                : f,
            ),
          );
          return;
        }
      }
      // Timeout — o processamento demorou demais.
      setFiles((prev) =>
        prev.map((f) =>
          f.result === baseResult && f.resultIndex === batchIdx
            ? { ...f, status: 'error' as const, error: t('upload.error.timeout') }
            : f,
        ),
      );
    },
    [t],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  const processAll = useCallback(async () => {
    setProcessing(true);
    const pendingIndices = files
      .map((f, i) => (f.status === 'pending' ? i : -1))
      .filter((i) => i >= 0);

    if (pendingIndices.length === 0) {
      setProcessing(false);
      return;
    }

    const pendingFiles = pendingIndices.map((i) => files[i].file);

    // Marca todos como uploading
    setFiles((prev) =>
      prev.map((f, i) =>
        pendingIndices.includes(i) ? { ...f, status: 'uploading', progress: 0 } : f,
      ),
    );

    try {
      // Issue #202: envia todos os arquivos em uma unica request (batch).
      // Todos compartilham o mesmo accession (lote AAA).
      const onUploaded = () => {
        setFiles((prev) =>
          prev.map((f, i) =>
            pendingIndices.includes(i) ? { ...f, status: 'processing' as const, progress: 100 } : f,
          ),
        );
      };
      const result = await uploadDocument(pendingFiles, (percent) => {
        setFiles((prev) =>
          prev.map((f, i) =>
            pendingIndices.includes(i) ? { ...f, progress: percent } : f,
          ),
        );
      }, undefined, undefined, onUploaded, clienteAtivoId);

      // Mapeia cada arquivo pendente para seu resultado no batch
      setFiles((prev) =>
        prev.map((f, i) => {
          const batchIdx = pendingIndices.indexOf(i);
          if (batchIdx < 0) return f;
          const doc = result.documents[batchIdx];
          const pipelineResult = result.results[batchIdx];
          if (doc && pipelineResult) {
            // Issue #276: resultado assincrono (placeholder sem `root`) —
            // mantem 'processing' e inicia polling ate concluido/erro.
            if (!('root' in pipelineResult)) {
              void pollAsyncDocument(doc.id, batchIdx, result);
              return {
                ...f,
                status: 'processing' as const,
                progress: 100,
                result,
                resultIndex: batchIdx,
              };
            }
            return {
              ...f,
              status: 'done' as const,
              progress: 100,
              result,
              resultIndex: batchIdx,
            };
          }
          return {
            ...f,
            status: 'error' as const,
            error: t('upload.error.batchNotFound'),
          };
        }),
      );

      // Issue #279: Se 3+ arquivos foram enviados com sucesso, ativa
      // modo batch — mostra toast, inicia polling de /batch-status e
      // bloqueia novos uploads ate o lote concluir.
      const createdDocs = result.documents.filter((d) => d && d.id);
      if (createdDocs.length >= 3) {
        showToast(t('upload.batch.submitted'), 'info');
        setBatchActive(true);
      }
    } catch (err) {
      // Issue #219: Se for DuplicateError, mostra modal de confirmacao.
      // Suporta tanto status='duplicate' (1 arquivo) quanto
      // status='batch_duplicates' (lista de duplicatas do lote).
      if (err instanceof DuplicateError) {
        setDuplicateInfo(err.info);
        setDuplicateFiles(pendingFiles);
        setDuplicatePendingIndices(pendingIndices);
        // Issue #219: Se for batch_duplicates, guarda a lista e inicializa
        // decisoes com 'cancel' para todas as duplicatas.
        if (err.info.status === 'batch_duplicates' && err.info.duplicates) {
          setBatchDuplicates(err.info.duplicates);
          const decisions: Record<number, 'replace' | 'archive' | 'duplicate' | 'cancel'> = {};
          for (const dup of err.info.duplicates) {
            decisions[dup.batch_index] = 'cancel';
          }
          setDuplicateDecisions(decisions);
        }
        // Volta os arquivos para pending (aguardando decisao do usuario)
        setFiles((prev) =>
          prev.map((f, i) =>
            pendingIndices.includes(i)
              ? { ...f, status: 'pending' as const, progress: 0 }
              : f,
          ),
        );
      } else {
        setFiles((prev) =>
          prev.map((f, i) =>
            pendingIndices.includes(i)
              ? { ...f, status: 'error' as const, error: err instanceof Error ? err.message : t('common.error') }
              : f,
          ),
        );
      }
    }
    setProcessing(false);
  }, [files]);

  // Issue #219: Re-envia com mode=replace|archive|duplicate apos confirmacao.
  const resolveDuplicate = useCallback(async (mode: 'replace' | 'archive' | 'duplicate') => {
    if (!duplicateFiles || duplicatePendingIndices.length === 0) return;
    setResolvingDuplicate(true);
    setDuplicateInfo(null);
    setProcessing(true);

    const pendingIndices = duplicatePendingIndices;
    const pendingFiles = duplicateFiles;

    setFiles((prev) =>
      prev.map((f, i) =>
        pendingIndices.includes(i) ? { ...f, status: 'uploading', progress: 0 } : f,
      ),
    );

    try {
      const onUploaded = () => {
        setFiles((prev) =>
          prev.map((f, i) =>
            pendingIndices.includes(i) ? { ...f, status: 'processing' as const, progress: 100 } : f,
          ),
        );
      };
      const result = await uploadDocument(pendingFiles, (percent) => {
        setFiles((prev) =>
          prev.map((f, i) =>
            pendingIndices.includes(i) ? { ...f, progress: percent } : f,
          ),
        );
      }, mode, undefined, onUploaded, clienteAtivoId);

      setFiles((prev) =>
        prev.map((f, i) => {
          const batchIdx = pendingIndices.indexOf(i);
          if (batchIdx < 0) return f;
          const doc = result.documents[batchIdx];
          const pipelineResult = result.results[batchIdx];
          if (doc && pipelineResult) {
            // Issue #276: resultado assincrono (placeholder sem `root`) —
            // mantem 'processing' e inicia polling ate concluido/erro.
            if (!('root' in pipelineResult)) {
              void pollAsyncDocument(doc.id, batchIdx, result);
              return {
                ...f,
                status: 'processing' as const,
                progress: 100,
                result,
                resultIndex: batchIdx,
              };
            }
            return {
              ...f,
              status: 'done' as const,
              progress: 100,
              result,
              resultIndex: batchIdx,
            };
          }
          return {
            ...f,
            status: 'error' as const,
            error: t('upload.error.batchNotFound'),
          };
        }),
      );

      // Issue #279: batch mode para 3+ arquivos apos resolver duplicata.
      const createdDocs = result.documents.filter((d) => d && d.id);
      if (createdDocs.length >= 3) {
        showToast(t('upload.batch.submitted'), 'info');
        setBatchActive(true);
      }
    } catch (err) {
      // Issue #219: Se for DuplicateError novamente (outra duplicata no lote),
      // mostra modal de confirmacao novamente em vez de [object Object].
      if (err instanceof DuplicateError) {
        setDuplicateInfo(err.info);
        setDuplicateFiles(pendingFiles);
        setDuplicatePendingIndices(pendingIndices);
        if (err.info.status === 'batch_duplicates' && err.info.duplicates) {
          setBatchDuplicates(err.info.duplicates);
          const decisions: Record<number, 'replace' | 'archive' | 'duplicate' | 'cancel'> = {};
          for (const dup of err.info.duplicates) {
            decisions[dup.batch_index] = 'cancel';
          }
          setDuplicateDecisions(decisions);
        }
        setFiles((prev) =>
          prev.map((f, i) =>
            pendingIndices.includes(i)
              ? { ...f, status: 'pending' as const, progress: 0 }
              : f,
          ),
        );
      } else {
        setFiles((prev) =>
          prev.map((f, i) =>
            pendingIndices.includes(i)
              ? { ...f, status: 'error' as const, error: err instanceof Error ? err.message : t('common.error') }
              : f,
          ),
        );
      }
    }

    setDuplicateFiles(null);
    setDuplicatePendingIndices([]);
    setBatchDuplicates([]);
    setDuplicateDecisions({});
    setProcessing(false);
    setResolvingDuplicate(false);
  }, [duplicateFiles, duplicatePendingIndices]);

  // Issue #219: Re-envia batch com modes por arquivo apos decisoes do usuario.
  // Cada duplicata pode ter uma decisao diferente (replace/archive/duplicate/cancel).
  // Arquivos nao-duplicados sao processados normalmente (mode='none' no backend
  // significa processar se nao for duplicata, pular se for).
  const resolveBatchDuplicates = useCallback(async () => {
    if (!duplicateFiles || duplicatePendingIndices.length === 0) return;
    setResolvingDuplicate(true);
    setDuplicateInfo(null);
    setProcessing(true);

    const pendingIndices = duplicatePendingIndices;
    const pendingFiles = duplicateFiles;

    // Construir modes array (mesmo tamanho do batch original).
    // 'none' = processar normalmente (nao-duplicatas) ou pular (duplicatas canceladas).
    const modes = Array(pendingFiles.length).fill('none');
    for (const dup of batchDuplicates) {
      const decision = duplicateDecisions[dup.batch_index];
      if (decision && decision !== 'cancel') {
        modes[dup.batch_index] = decision;
      }
    }

    setFiles((prev) =>
      prev.map((f, i) =>
        pendingIndices.includes(i) ? { ...f, status: 'uploading', progress: 0 } : f,
      ),
    );

    // Issue #280: flag local para erro de rede — controla se devemos
    // limpar o estado e re-habilitar o botao ao final.
    let networkError = false;
    setBatchNetworkError(false);

    try {
      const onUploaded = () => {
        setFiles((prev) =>
          prev.map((f, i) =>
            pendingIndices.includes(i) ? { ...f, status: 'processing' as const, progress: 100 } : f,
          ),
        );
      };
      const result = await uploadDocument(pendingFiles, (percent) => {
        setFiles((prev) =>
          prev.map((f, i) =>
            pendingIndices.includes(i) ? { ...f, progress: percent } : f,
          ),
        );
      }, undefined, modes, onUploaded, clienteAtivoId);

      setFiles((prev) =>
        prev.map((f, i) => {
          const batchIdx = pendingIndices.indexOf(i);
          if (batchIdx < 0) return f;
          const doc = result.documents[batchIdx];
          const pipelineResult = result.results[batchIdx];
          if (doc && pipelineResult) {
            // Issue #276: resultado assincrono (placeholder sem `root`) —
            // mantem 'processing' e inicia polling ate concluido/erro.
            if (!('root' in pipelineResult)) {
              void pollAsyncDocument(doc.id, batchIdx, result);
              return {
                ...f,
                status: 'processing' as const,
                progress: 100,
                result,
                resultIndex: batchIdx,
              };
            }
            return {
              ...f,
              status: 'done' as const,
              progress: 100,
              result,
              resultIndex: batchIdx,
            };
          }
          // Arquivos pulados (mode=none/cancel para duplicatas)
          if (result.duplicates[batchIdx] && !doc) {
            return {
              ...f,
              status: 'done' as const,
              progress: 100,
              result,
              resultIndex: batchIdx,
              error: t('upload.error.cancelledDuplicate'),
            };
          }
          return {
            ...f,
            status: 'error' as const,
            error: t('upload.error.batchNotFound'),
          };
        }),
      );

      // Issue #279: batch mode para 3+ arquivos apos resolver duplicatas do lote.
      const createdDocs = result.documents.filter((d) => d && d.id);
      if (createdDocs.length >= 3) {
        showToast(t('upload.batch.submitted'), 'info');
        setBatchActive(true);
      }
    } catch (err) {
      // Issue #219: Se for DuplicateError novamente, mostra modal novamente.
      if (err instanceof DuplicateError) {
        setDuplicateInfo(err.info);
        setDuplicateFiles(pendingFiles);
        setDuplicatePendingIndices(pendingIndices);
        if (err.info.status === 'batch_duplicates' && err.info.duplicates) {
          setBatchDuplicates(err.info.duplicates);
          const decisions: Record<number, 'replace' | 'archive' | 'duplicate' | 'cancel'> = {};
          for (const dup of err.info.duplicates) {
            decisions[dup.batch_index] = 'cancel';
          }
          setDuplicateDecisions(decisions);
        }
        setFiles((prev) =>
          prev.map((f, i) =>
            pendingIndices.includes(i)
              ? { ...f, status: 'pending' as const, progress: 0 }
              : f,
          ),
        );
      } else {
        // Issue #280: Erro de rede (sem resposta, timeout, HTTP 499).
        // O lote pode estar sendo processado no servidor. NAO marcar arquivos
        // como erro, NAO limpar duplicate state, NAO re-habilitar o botao —
        // o usuario deve verificar a lista de documentos antes de re-enviar
        // para evitar subprocesses duplicados.
        networkError = true;
        setBatchNetworkError(true);
        showToast(t('upload.batch.networkError'), 'error');
      }
    }

    // Issue #280: Em erro de rede, nao limpar o duplicate state nem
    // re-habilitar o botao de upload — o lote pode ainda estar processando
    // no servidor. O usuario usa o botao 'Verificar status' para conferir.
    if (!networkError) {
      setDuplicateFiles(null);
      setDuplicatePendingIndices([]);
      setBatchDuplicates([]);
      setDuplicateDecisions({});
      setProcessing(false);
    }
    setResolvingDuplicate(false);
  }, [duplicateFiles, duplicatePendingIndices, batchDuplicates, duplicateDecisions, t]);

  // Issue #219: Cancelar duplicata — volta arquivos para pending.
  const cancelDuplicate = useCallback(() => {
    setDuplicateInfo(null);
    setDuplicateFiles(null);
    setDuplicatePendingIndices([]);
    setBatchDuplicates([]);
    setDuplicateDecisions({});
  }, []);

  const removeFile = useCallback((idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const clearAll = useCallback(() => {
    setFiles([]);
    setSelectedResult(null);
  }, []);

  const doneCount = files.filter((f) => f.status === 'done').length;
  const errorCount = files.filter((f) => f.status === 'error').length;
  const pendingCount = files.filter((f) => f.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Issue #279: Toast simples para notificacoes de batch. */}
      {toast && (
        <div
          className={`fixed right-4 top-4 z-50 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg ${
            toast.type === 'success'
              ? 'bg-green-600'
              : toast.type === 'error'
                ? 'bg-red-600'
                : 'bg-brand-700'
          }`}
          role="alert"
        >
          {toast.message}
        </div>
      )}

      <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
        <h1 className="mb-2 text-2xl font-bold text-brand-900">{t('upload.title')}</h1>
        <p className="mb-4 text-sm text-brand-600">
          {t('upload.subtitle')}
        </p>

        {/* Issue #279: Banner de lote em processamento. */}
        {batchActive && batchStatus && batchStatus.active && (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
            {t('upload.batch.inProgress', {
              done: (batchStatus.concluido ?? 0) + (batchStatus.erro ?? 0),
              total: batchStatus.total ?? 0,
            })}
          </div>
        )}

        {/* Issue #280: Aviso de erro de rede no envio de batch. O lote pode
            estar sendo processado no servidor — nao re-habilitar o botao. */}
        {batchNetworkError && (
          <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            <p>{t('upload.batch.networkError')}</p>
            <button
              type="button"
              onClick={() => onNavigate?.('documents')}
              className="mt-2 rounded-md border border-amber-400 bg-amber-100 px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-200"
            >
              {t('upload.batch.checkStatus')}
            </button>
          </div>
        )}

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => !batchActive && inputRef.current?.click()}
          className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
            batchActive
              ? 'cursor-not-allowed border-brand-200 bg-brand-50 opacity-60'
              : dragging
                ? 'cursor-pointer border-brand-500 bg-brand-50'
                : 'cursor-pointer border-brand-300 hover:border-brand-400 hover:bg-brand-50'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPTED_EXTENSIONS.join(',')}
            onChange={(e) => e.target.files && addFiles(e.target.files)}
            className="hidden"
            disabled={batchActive}
          />
          <div className="text-brand-500">
            <svg className="mx-auto mb-3 h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <p className="text-sm font-medium text-brand-700">
              {batchActive ? t('upload.batch.blocked') : t('upload.dropzone.text')}
            </p>
            <p className="mt-1 text-xs text-brand-400">
              {t('upload.dropzone.formats', { formats: ACCEPTED_EXTENSIONS.join(', ') })}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        {files.length > 0 && (
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={processAll}
              disabled={processing || pendingCount === 0 || batchActive}
              title={batchActive ? t('upload.batch.blocked') : undefined}
              className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50"
            >
              {processing ? t('upload.processing') : t('upload.process', { count: pendingCount })}
            </button>
            <button
              type="button"
              onClick={clearAll}
              disabled={processing}
              className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
            >
              {t('upload.clear')}
            </button>
            <span className="ml-auto text-sm text-brand-500">
              {t('upload.summary', { done: doneCount, errors: errorCount, pending: pendingCount })}
            </span>
          </div>
        )}
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-brand-800">{t('upload.filesTitle')}</h2>
          <div className="space-y-3">
            {files.map((f, idx) => {
              // Issue #276: `results[i]` pode ser AsyncResultPlaceholder
              // ({status:"pendente", async:true}) — sem `root`/`total_errors`.
              // Mesmo narrowing `'root' in` usado em processAll/resolveDuplicate.
              const resultEntry =
                f.result && f.resultIndex !== undefined
                  ? f.result.results[f.resultIndex]
                  : undefined;
              const pipeline =
                resultEntry && 'root' in resultEntry ? resultEntry : null;
              return (
              <div key={idx} className="rounded-md border border-brand-100 p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-brand-800">{f.file.name}</span>
                    <span className="text-xs text-brand-400">
                      {(f.file.size / 1024).toFixed(0)} KB
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={f.status} />
                    {f.status === 'done' && pipeline && (
                      <button
                        type="button"
                        onClick={() => setSelectedResult(pipeline)}
                        className="text-xs text-brand-700 underline"
                      >
                        {t('upload.viewResult')}
                      </button>
                    )}
                    {/* Issue #267: esconde "remover" enquanto ha request em
                        voo (processing=true). Remover um arquivo done/error
                        durante o upload desloca os indices e quebra o match
                        com pendingIndices, deixando arquivos presos. */}
                    {!processing && f.status !== 'uploading' && f.status !== 'processing' && (
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        {t('upload.remove')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Progress bar */}
                {f.status === 'uploading' && (
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-brand-100">
                    <div
                      className="h-full bg-brand-600 transition-all"
                      style={{ width: `${f.progress}%` }}
                    />
                  </div>
                )}

                {/* Error */}
                {f.status === 'error' && f.error && (
                  <p className="mt-2 text-xs text-red-700">{f.error}</p>
                )}

                {/* Result summary */}
                {f.status === 'done' && f.result && f.resultIndex !== undefined && pipeline && (
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <span className="rounded bg-brand-100 px-2 py-0.5 text-brand-700">
                      {t('upload.result.type', { type: pipeline.root.document_type })}
                    </span>
                    <span className="rounded bg-brand-100 px-2 py-0.5 text-brand-700">
                      {t('upload.result.origin', { origin: pipeline.root.source_type })}
                    </span>
                    <span className="rounded bg-brand-100 px-2 py-0.5 text-brand-700">
                      {t('upload.result.fields', { count: Object.keys(pipeline.root.fields).length })}
                    </span>
                    {f.result.documents[f.resultIndex]?.import_id && (
                      <span className="rounded bg-blue-100 px-2 py-0.5 font-mono text-blue-700">
                        {f.result.documents[f.resultIndex]?.import_id}
                      </span>
                    )}
                    {pipeline.total_errors > 0 && (
                      <span className="rounded bg-red-100 px-2 py-0.5 text-red-700">
                        {t('upload.result.errors', { count: pipeline.total_errors })}
                      </span>
                    )}
                    {f.result.duplicates[f.resultIndex] && (
                      <span className="rounded bg-yellow-100 px-2 py-0.5 text-yellow-800">
                        {t('upload.result.duplicate')}
                      </span>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Issue #219: Modal de confirmacao de duplicata — batch (lista) */}
      {duplicateInfo && duplicateInfo.status === 'batch_duplicates' && batchDuplicates.length > 0 && (
        <BatchDuplicateModal
          duplicates={batchDuplicates}
          decisions={duplicateDecisions}
          onDecisionChange={(batchIndex, decision) =>
            setDuplicateDecisions((prev) => ({ ...prev, [batchIndex]: decision }))
          }
          onConfirm={resolveBatchDuplicates}
          onCancel={cancelDuplicate}
          resolving={resolvingDuplicate}
          userRole={userRole}
        />
      )}

      {/* Issue #219: Modal de confirmacao de duplicata — arquivo unico */}
      {duplicateInfo && duplicateInfo.status === 'duplicate' && (
        <DuplicateModal
          info={duplicateInfo}
          onReplace={() => resolveDuplicate('replace')}
          onArchive={() => resolveDuplicate('archive')}
          onDuplicate={() => resolveDuplicate('duplicate')}
          onCancel={cancelDuplicate}
          resolving={resolvingDuplicate}
        />
      )}

      {/* Result modal/panel */}
      {selectedResult && (
        <ResultPanel result={selectedResult} onClose={() => setSelectedResult(null)} />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const t = useT();
  const styles: Record<string, string> = {
    pending: 'bg-brand-100 text-brand-600',
    uploading: 'bg-blue-100 text-blue-700',
    processing: 'bg-blue-100 text-blue-700',
    done: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700',
  };
  const labels: Record<string, string> = {
    pending: t('upload.status.pending'),
    uploading: t('upload.status.uploading'),
    processing: t('upload.status.processing'),
    done: t('upload.status.done'),
    error: t('upload.status.error'),
  };
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </span>
  );
}

function ResultPanel({ result, onClose }: { result: PipelineResultData; onClose: () => void }) {
  const t = useT();
  const root = result.root ?? {};
  const fieldEntries = Object.entries(root.fields ?? {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[80vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-brand-900">{t('upload.result.title')}</h2>
          <button type="button" onClick={onClose} className="text-brand-500 hover:text-brand-800">
            {t('upload.result.close')}
          </button>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <div className="rounded bg-brand-50 p-2">
            <span className="text-brand-500">{t('upload.result.file')}</span>
            <p className="font-medium text-brand-800">{root.file}</p>
          </div>
          <div className="rounded bg-brand-50 p-2">
            <span className="text-brand-500">{t('upload.result.typeLabel')}</span>
            <p className="font-medium text-brand-800">{root.document_type}</p>
          </div>
          <div className="rounded bg-brand-50 p-2">
            <span className="text-brand-500">{t('upload.result.originLabel')}</span>
            <p className="font-medium text-brand-800">{root.source_type}</p>
          </div>
          <div className="rounded bg-brand-50 p-2">
            <span className="text-brand-500">{t('upload.result.time')}</span>
            <p className="font-medium text-brand-800">{root.processing_time_ms}ms</p>
          </div>
        </div>

        {root.needs_review && (
          <div className="mb-4 rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800">
            {t('upload.result.needsReview', { reason: root.review_reason || t('upload.result.reviewReasonDefault') })}
          </div>
        )}

        {root.error && (
          <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {t('upload.result.error', { error: root.error })}
          </div>
        )}

        {/* Fields table */}
        {fieldEntries.length > 0 && (
          <div className="mb-4">
            <h3 className="mb-2 font-semibold text-brand-800">{t('upload.result.extractedFields')}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-brand-200 text-brand-700">
                  <tr>
                    <th className="py-2 pr-3">{t('upload.result.col.field')}</th>
                    <th className="py-2 pr-3">{t('upload.result.col.value')}</th>
                    <th className="py-2 pr-3">{t('upload.result.col.confidence')}</th>
                    <th className="py-2 pr-3">{t('upload.result.col.origin')}</th>
                  </tr>
                </thead>
                <tbody>
                  {fieldEntries.map(([name, field]) => (
                    <tr key={name} className="border-b border-brand-100">
                      <td className="py-2 pr-3 font-medium text-brand-800">{name}</td>
                      <td className="py-2 pr-3 text-brand-700">
                        {field.value !== null && field.value !== undefined
                          ? String(field.value)
                          : '—'}
                      </td>
                      <td className="py-2 pr-3">
                        <ConfidenceBar value={field.confidence} />
                      </td>
                      <td className="py-2 pr-3 text-xs text-brand-500">{field.source || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Metadata */}
        {Object.keys(root.metadata ?? {}).length > 0 && (
          <div className="mb-4">
            <h3 className="mb-2 font-semibold text-brand-800">{t('upload.result.metadata')}</h3>
            <pre className="overflow-x-auto rounded bg-brand-50 p-3 text-xs text-brand-700">
              {JSON.stringify(root.metadata ?? {}, null, 2)}
            </pre>
          </div>
        )}

        {/* Children */}
        {(root.children ?? []).length > 0 && (
          <div>
            <h3 className="mb-2 font-semibold text-brand-800">
              {t('upload.result.children', { count: (root.children ?? []).length })}
            </h3>
            <div className="space-y-2">
              {(root.children ?? []).map((child, i) => (
                <div key={i} className="rounded border border-brand-100 p-3 text-sm">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-brand-800">{child.file}</span>
                    <span className="text-xs text-brand-500">{child.document_type}</span>
                    <span className="text-xs text-brand-500">{child.source_type}</span>
                    {child.error && <span className="text-xs text-red-600">{t('upload.result.childError')}</span>}
                  </div>
                  {Object.keys(child.fields).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-2 text-xs">
                      {Object.entries(child.fields).map(([k, v]) => (
                        <span key={k} className="rounded bg-brand-50 px-2 py-0.5 text-brand-600">
                          {k}: {v.value !== null ? String(v.value) : '—'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-16 overflow-hidden rounded-full bg-brand-100">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-brand-600">{pct}%</span>
    </div>
  );
}

// Issue #219: Modal de confirmacao para NF duplicada — 4 opcoes.
function DuplicateModal({
  info,
  onReplace,
  onArchive,
  onDuplicate,
  onCancel,
  resolving,
}: {
  info: DuplicateInfo;
  onReplace: () => void;
  onArchive: () => void;
  onDuplicate: () => void;
  onCancel: () => void;
  resolving: boolean;
}) {
  const t = useT();
  const { formatDateTime: fmtDateTime } = useFormatters();

  const typeLabel = info.duplicate_type === 'file' ? t('upload.duplicate.type.file') : t('upload.duplicate.type.data');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
            <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-brand-900">{t('upload.duplicate.title')}</h2>
        </div>

        <p className="mb-4 text-sm text-brand-700">
          {t('upload.duplicate.intro')}
        </p>

        <div className="mb-4 rounded-md border border-brand-200 bg-brand-50 p-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-brand-500">{t('upload.duplicate.importId')}</span>
              <p className="font-mono font-medium text-brand-800">{info.existing_import_id}</p>
            </div>
            <div>
              <span className="text-brand-500">{t('upload.duplicate.type')}</span>
              <p className="font-medium text-brand-800">{typeLabel}</p>
            </div>
            <div>
              <span className="text-brand-500">{t('upload.duplicate.file')}</span>
              <p className="font-medium text-brand-800">{info.existing_filename}</p>
            </div>
            <div>
              <span className="text-brand-500">{t('upload.duplicate.processedAt')}</span>
              <p className="font-medium text-brand-800">{fmtDateTime(info.existing_created_at)}</p>
            </div>
          </div>
        </div>

        <p className="mb-3 text-sm font-medium text-brand-800">{t('upload.duplicate.whatToDo')}</p>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onReplace}
            disabled={resolving}
            className="rounded-md bg-brand-700 px-4 py-2 text-left text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50"
          >
            <span className="font-semibold">{t('upload.duplicate.replace')}</span>
            <span className="block text-xs font-normal opacity-90">{t('upload.duplicate.replaceDesc')}</span>
          </button>
          <button
            type="button"
            onClick={onArchive}
            disabled={resolving}
            className="rounded-md border border-brand-300 px-4 py-2 text-left text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          >
            <span className="font-semibold">{t('upload.duplicate.archive')}</span>
            <span className="block text-xs font-normal text-brand-500">{t('upload.duplicate.archiveDesc')}</span>
          </button>
          <button
            type="button"
            onClick={onDuplicate}
            disabled={resolving}
            className="rounded-md border border-brand-300 px-4 py-2 text-left text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          >
            <span className="font-semibold">{t('upload.duplicate.duplicate')}</span>
            <span className="block text-xs font-normal text-brand-500">{t('upload.duplicate.duplicateDesc')}</span>
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={resolving}
            className="rounded-md border border-brand-300 px-4 py-2 text-left text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          >
            <span className="font-semibold">{t('upload.duplicate.cancel')}</span>
            <span className="block text-xs font-normal text-brand-500">{t('upload.duplicate.cancelDesc')}</span>
          </button>
        </div>

        {resolving && (
          <p className="mt-3 text-center text-xs text-brand-500">{t('upload.duplicate.processing')}</p>
        )}
      </div>
    </div>
  );
}

// Issue #219: Modal de confirmacao para batch duplicates — lista com
// seletor individual (substituir/arquivar/duplicar/cancelar) por arquivo.
function BatchDuplicateModal({
  duplicates,
  decisions,
  onDecisionChange,
  onConfirm,
  onCancel,
  resolving,
  userRole,
}: {
  duplicates: BatchDuplicate[];
  decisions: Record<number, 'replace' | 'archive' | 'duplicate' | 'cancel'>;
  onDecisionChange: (batchIndex: number, decision: 'replace' | 'archive' | 'duplicate' | 'cancel') => void;
  onConfirm: () => void;
  onCancel: () => void;
  resolving: boolean;
  userRole?: string;
}) {
  const t = useT();
  const { formatDateTime: fmtDateTime } = useFormatters();

  const decisionLabels: Record<string, { label: string; desc: string }> = {
    replace: { label: t('upload.batchDuplicate.replace'), desc: t('upload.batchDuplicate.replaceDesc') },
    archive: { label: t('upload.batchDuplicate.archive'), desc: t('upload.batchDuplicate.archiveDesc') },
    duplicate: { label: t('upload.batchDuplicate.duplicate'), desc: t('upload.batchDuplicate.duplicateDesc') },
    cancel: { label: t('upload.batchDuplicate.cancel'), desc: t('upload.batchDuplicate.cancelDesc') },
  };

  const optionList: ReadonlyArray<'replace' | 'archive' | 'duplicate' | 'cancel'> = ['replace', 'archive', 'duplicate', 'cancel'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
            <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-brand-900">
            {t('upload.batchDuplicate.title', { count: duplicates.length })}
          </h2>
        </div>

        <p className="mb-4 text-sm text-brand-700">
          {t('upload.batchDuplicate.intro')}
        </p>

        <div className="space-y-3">
          {duplicates.map((dup) => {
            const decision = decisions[dup.batch_index] || 'cancel';
            const typeLabel = dup.duplicate_type === 'file' ? t('upload.batchDuplicate.type.file') : t('upload.batchDuplicate.type.data');
            return (
              <div key={dup.batch_index} className="rounded-md border border-brand-200 bg-brand-50 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-brand-800">{dup.uploaded_filename}</span>
                  <span className="text-xs text-brand-500">{typeLabel}</span>
                </div>
                <div className="mb-3 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-brand-500">{t('upload.batchDuplicate.existingImportId')}</span>
                    <p className="font-mono font-medium text-brand-800">{dup.existing_import_id}</p>
                  </div>
                  <div>
                    <span className="text-brand-500">{t('upload.batchDuplicate.existingFile')}</span>
                    <p className="font-medium text-brand-800">{dup.existing_filename}</p>
                  </div>
                  <div>
                    <span className="text-brand-500">{t('upload.batchDuplicate.processedAt')}</span>
                    <p className="font-medium text-brand-800">{fmtDateTime(dup.existing_created_at)}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {optionList.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => onDecisionChange(dup.batch_index, opt)}
                      disabled={resolving}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                        decision === opt
                          ? opt === 'replace'
                            ? 'bg-brand-700 text-white'
                            : opt === 'cancel'
                            ? 'bg-red-100 text-red-700 border border-red-300'
                            : 'bg-brand-100 text-brand-800 border border-brand-400'
                          : 'border border-brand-300 text-brand-600 hover:bg-brand-50'
                      }`}
                    >
                      {decisionLabels[opt].label}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-brand-400">
                  {decisionLabels[decision].desc}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={resolving}
            className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          >
            {t('upload.batchDuplicate.cancelAll')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={resolving}
            className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50"
          >
            {resolving ? t('upload.batchDuplicate.processing') : t('upload.batchDuplicate.confirm')}
          </button>
        </div>

        {resolving && (
          <p className="mt-3 text-center text-xs text-brand-500">{t('upload.batchDuplicate.processing')}</p>
        )}
      </div>
    </div>
  );
}

export default Upload;
